import type {
  CreateGraduationAssessmentSessionInput,
  GraduationAssessmentQuery,
  GraduationAssessmentResponse,
  GraduationAssessmentSession,
  InvalidateGraduationAssessmentInput,
} from './types'

function requireDraft(session: GraduationAssessmentSession): void {
  if (session.status !== 'draft') {
    throw new Error(`Graduation assessment session ${session.id} is not a draft.`)
  }
}

function responseBelongsToSession(
  session: GraduationAssessmentSession,
  response: GraduationAssessmentResponse,
): boolean {
  const attempt = response.attempt
  return attempt.studentId === session.studentId
    && attempt.stageId === session.stageId
    && attempt.unitId === session.unitId
    && attempt.assessmentSessionId === session.id
    && attempt.evidenceKind === 'summative'
}

export function createGraduationAssessmentSession(
  input: CreateGraduationAssessmentSessionInput,
): GraduationAssessmentSession {
  const questionManifest = [...input.questionManifest]
  if (questionManifest.length === 0 || new Set(questionManifest).size !== questionManifest.length) {
    throw new Error('Graduation assessment question manifest must be non-empty and unique.')
  }
  const startedAt = input.startedAt ?? new Date().toISOString()
  return {
    ...input,
    questionManifest,
    responses: [],
    status: 'draft',
    startedAt,
    updatedAt: startedAt,
    completedAt: null,
    invalidation: null,
    integrity: 'native',
  }
}

export function submitGraduationAssessmentResponse(
  session: GraduationAssessmentSession,
  response: GraduationAssessmentResponse,
): GraduationAssessmentSession {
  requireDraft(session)
  if (!session.questionManifest.includes(response.questionId)) {
    throw new Error(`Question ${response.questionId} is not part of the frozen assessment form.`)
  }
  if (session.responses.some((existing) => existing.questionId === response.questionId)) {
    throw new Error(`Question ${response.questionId} has already been submitted.`)
  }
  if (!responseBelongsToSession(session, response)) {
    throw new Error('Graduation assessment response does not belong to this session.')
  }
  if (response.attempt.retryCount !== 0) {
    throw new Error('Graduation assessment responses must be first submissions with retryCount 0.')
  }
  return {
    ...session,
    responses: [...session.responses, response],
    updatedAt: response.attempt.completedAt,
  }
}

export function completeGraduationAssessmentSession(
  session: GraduationAssessmentSession,
  completedAt = new Date().toISOString(),
): GraduationAssessmentSession {
  requireDraft(session)
  const answered = new Set(session.responses.map((response) => response.questionId))
  const complete = session.responses.length === session.questionManifest.length
    && session.questionManifest.every((questionId) => answered.has(questionId))
  if (!complete) {
    throw new Error(`Graduation assessment session ${session.id} is not complete.`)
  }
  return {
    ...session,
    status: 'completed',
    completedAt,
    updatedAt: completedAt,
  }
}

export function invalidateGraduationAssessmentSession(
  session: GraduationAssessmentSession,
  input: InvalidateGraduationAssessmentInput,
): GraduationAssessmentSession {
  requireDraft(session)
  if (input.actorRole === 'student') {
    throw new Error('Only a guardian or teacher can invalidate a graduation assessment.')
  }
  const reason = input.reason.trim()
  if (!reason) {
    throw new Error('Graduation assessment invalidation requires a reason.')
  }
  const invalidatedAt = input.invalidatedAt ?? new Date().toISOString()
  return {
    ...session,
    status: 'invalidated',
    updatedAt: invalidatedAt,
    invalidation: {
      actorId: input.actorId,
      actorRole: input.actorRole ?? 'teacher',
      reason,
      invalidatedAt,
    },
  }
}

export function findLatestCompletedGraduationAssessment(
  sessions: readonly GraduationAssessmentSession[],
  query: GraduationAssessmentQuery,
): GraduationAssessmentSession | null {
  return sessions
    .filter((session) => (
      session.status === 'completed'
      && session.studentId === query.studentId
      && session.stageId === query.stageId
      && session.unitId === query.unitId
      && session.completedAt
    ))
    .sort((left, right) => (
      new Date(right.completedAt as string).getTime()
      - new Date(left.completedAt as string).getTime()
    ))[0] ?? null
}
